import { useState, useEffect } from 'react';
import { connect } from 'react-redux';

import ResourceModal from 'app/components/ResourceModal';
import ResourceModalPanel from 'app/components/ResourceModalPanel';
import BackgroundList from 'app/components/BackgroundList';
import OverlayList from 'app/components/OverlayList';
import KnockoutPairingForm from 'app/components/KnockoutPairingForm';
import JobConfigForm from './JobConfigForm';
import VolumePricingForm from 'app/components/VolumePricingForm';
import DiscountForm from 'app/components/DiscountForm';

import {
  OVERLAY_PRODUCT_TYPE,
  KNOCKOUT_PRODUCT_TYPE,
} from 'app/utils/Constants';


const JobConfigPanel = props => {
  const {
    jobConfig,
    jobConfigTemplate,
    action,
    openFormCallback,
    pricingModel
  } = props;

  // if we're creating a jobConfig then no actions panel will be shown
  if (action === 'create') {
    return null;
  }

  let cards = [];

  if (jobConfigTemplate.description) {
    cards.push({
      hideBorder: true,
      message: (
        <div>
          <h4>Product description</h4>
          <p>{jobConfigTemplate.description}</p>
        </div>
      )
    });
  }

  if (jobConfigTemplate.instructions) {
    cards.push({
      hideBorder: true,
      message: (
        <div>
          <h4>How to use this product</h4>
          <p>{jobConfigTemplate.instructions}</p>
        </div>
      )
    });
  }

  if ([OVERLAY_PRODUCT_TYPE, KNOCKOUT_PRODUCT_TYPE].includes(jobConfigTemplate.product_type)) {
    cards.push({
      label: 'File',
      message: 'Overlays',
      actions: [
        { label: 'Manage', callback: () => { openFormCallback('overlay'); } }
      ]
    })
  }

  if (jobConfigTemplate.product_type == KNOCKOUT_PRODUCT_TYPE) {
    cards.push({
      label: 'File',
      message: 'Background',
      actions: [
        { label: 'Manage', callback: () => { openFormCallback('bg'); } }
      ]
    })

    cards.push({
      message: 'Knockout Pairing',
      actions: [
        { label: 'Set-up Pairs', callback: () => { openFormCallback('pairing'); } }
      ]
    })
  }

  if (jobConfigTemplate.selling_method > 0) {
    if (pricingModel !== 0 ) {
      cards.push({
        label: '',
        message: 'Package Pricing',
        details: [
          { label: 'Discount is applied to the entire order' },
        ],
        actions: [
          { label: 'Update Package Pricing', callback: () => { openFormCallback('pricing'); } }
        ]
      })
    }

    cards.push({
      label: '',
      message: 'Discount Codes',
      actions: [
        { label: 'Update Discount Codes', callback: () => { openFormCallback('discounts'); } }
      ]
    })
  }

  return <ResourceModalPanel cards={cards} />;
}

const JobConfigModal = props => {
  const {
    isOpen,
    onClose,
    onFormSubmit,
    jobConfig,
    jobConfigTemplates,
  } = props;

  const getJobConfigTemplate = id => {
    if (id) {
      return jobConfigTemplates.find(jct => jct.id === id);
    } else {
      return jobConfigTemplates.filter(jct => jct.id !== -1)[0];
    }
  }

  const [state, setState] = useState({
    showBgForm: false,
    showOverlayForm: false,
    showPairingForm: false,
    showVolumePricingForm: false,
    showDiscountForm: false,
    pricingModel: null,
    jobConfigTemplate: getJobConfigTemplate(jobConfig?.job_config_template_id),
    lastUpdate: Date.now()
  });

  const setShowBgForm = val => setState(state => ({ ...state, showBgForm: val }));
  const setShowOverlayForm = val => setState(state => ({ ...state, showOverlayForm: val }));
  const setShowPairingForm = val => setState(state => ({ ...state, showPairingForm: val }));
  const setShowVolumePricingForm = val => setState(state => ({ ...state, showVolumePricingForm: val }));
  const setShowDiscountForm = val => setState(state => ({ ...state, showDiscountForm: val }));

  useEffect(() => {
    setState(state => ({
      ...state,
      pricingModel: jobConfig?.product?.pricing_model,
      jobConfigTemplate: getJobConfigTemplate(jobConfig?.job_config_template_id),
      lastUpdate: Date.now()
    }));
  }, [jobConfig, jobConfigTemplates]);

  const action = jobConfig ? 'update' : 'create';

  let title, subTitle = null;
  if (jobConfig) {
    title = jobConfig.name;

    // set sub title.
    // if Digital Delivery (default / free), show preset message,
    // otherwise summarize pricing.
    if (state.jobConfigTemplate?.is_default) {
      subTitle = 'Already paid / free';
    } else {
      subTitle = `${state.jobConfigTemplate?.name} sale, $${jobConfig.product.price} per image`;
      if (jobConfig.product.pricing_tiers?.length) {
        subTitle += ' with packages';
      } else if (jobConfig.product.discount) {
        subTitle += `, ${jobConfig.product.discount} for all`;
      }
    }

  } else {
    title = 'Add Product';
  }

  const openFormCallback = (type) => {
    switch (type) {
      case 'bg':
        setShowBgForm(true);
        break;
      case 'overlay':
        setShowOverlayForm(true);
        break;
      case 'pairing':
        setShowPairingForm(true);
        break;
      case 'pricing':
        setShowVolumePricingForm(true);
        break;
      case 'discounts':
        setShowDiscountForm(true);
        break;
    }
  }

  let backgrounds = null;
  let overlays = null;
  let pairs = null;
  let pricing = null;
  let discounts = null;

  if (jobConfig?.product_id && [OVERLAY_PRODUCT_TYPE, KNOCKOUT_PRODUCT_TYPE].includes(state.jobConfigTemplate?.product_type)) {
    overlays = <OverlayList isOpen={state.showOverlayForm} selectedJobConfig={jobConfig} onClose={() => setShowOverlayForm(false)} />
  }

  if (jobConfig?.product_id && state.jobConfigTemplate?.product_type == KNOCKOUT_PRODUCT_TYPE) {
    backgrounds = <BackgroundList isOpen={state.showBgForm} selectedJobConfig={jobConfig} onClose={() => setShowBgForm(false)} />
    pairs = <KnockoutPairingForm isOpen={state.showPairingForm} selectedJobConfig={jobConfig} onClose={() => setShowPairingForm(false)} />
  }

  if (jobConfig?.product_id && state.jobConfigTemplate?.selling_method > 0) {
    pricing = <VolumePricingForm selectedPriceModel={state.pricingModel} isOpen={state.showVolumePricingForm} selectedJobConfig={jobConfig} onClose={() => setShowVolumePricingForm(false)} />

    discounts = <DiscountForm isOpen={state.showDiscountForm} selectedJobConfig={jobConfig} onClose={() => setShowDiscountForm(false)} />
  }

  const onSubmit = params => onFormSubmit(action, params);

  const onJobConfigTemplateChange = (val) => {
    let jct = getJobConfigTemplate(val);
    setState(state => ({ ...state, jobConfigTemplate: jct, lastUpdate: Date.now() }));
  }

  const onPricingModelChange = val => {
    setState(state => ({ ...state, pricingModel: val }));
  }

  // hide form for Digital Delivery
  let hideForm = state.jobConfigTemplate?.is_default;

  return (
    <ResourceModal {...{ isOpen, onClose, title, subTitle }}>
      <JobConfigPanel {...{
        jobConfig,
        jobConfigTemplate: state.jobConfigTemplate,
        action,
        onClose,
        openFormCallback,
        pricingModel: state.pricingModel
      }} />
      {!hideForm && <JobConfigForm {...{
        jobConfig,
        jobConfigTemplate: state.jobConfigTemplate,
        jobConfigTemplates,
        onJobConfigTemplateChange,
        onPricingModelChange,
        action,
        onClose,
        onSubmit,
        lastUpdate: state.lastUpdate
      }} />}
      {backgrounds}
      {overlays}
      {pairs}
      {pricing}
      {discounts}
    </ResourceModal>
  );
}

const mapStateToProps = state => ({
  // need to figure out a different way to load these
  //jobConfigTemplates: selectJobConfigTemplates(state)
});

export default connect(mapStateToProps)(JobConfigModal);
